import { Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import TextField from '../../components/TextField/TextField';
import {
  SignInSx,
  PaperSx,
  TitleSx,
  ExplanationTextSx,
  PasswordSx,
  SaveButtonSx,
  EyeContainerSx,
  formSchema,
  FormData,
  ErrorMessageSx,
  CancelSx,
} from './ResetPasswordProps';
import EyeIcon from '../../assets/icons/EyeIcon';
import EyeClosedIcon from '../../assets/icons/EyeClosedIcon';
import { useCustomForm } from '../../hooks/useCustomForm';
import ControlledField from '../../components/ControlledField/ControlledField';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TestIds } from '../../utils/dataTestIds';
import { useSetAtom } from 'jotai';
import { showSnackbarAtom } from '../../store/snackbarStore';
import { resetPassword } from '../../apiClient/auth';
import {
  getApiErrorMessage,
  getValidationIssueErrors,
} from '../../utils/backendValidationIssues';

function ResetPassword() {
  const showSnackbar = useSetAtom(showSnackbarAtom);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { control, handleSubmit } = useCustomForm(
    formSchema,
    {},
    {
      defaultValues: {
        newPassword: '',
        confirmPassword: '',
      },
    }
  );
  const [newPasswordIsVisible, setNewPasswordIsVisible] = useState(false);
  const [confirmedPasswordIsVisible, setConfirmedPasswordIsVisible] =
    useState(false);
  const [submitError, setSubmitError] = useState('');

  async function handleLogin(formData: FormData) {
    try {
      setSubmitError('');
      if (formData.newPassword !== formData.confirmPassword) {
        setSubmitError('Passwords do not match');
        return;
      }

      await resetPassword({
        newPassword: formData.newPassword,
        token: searchParams.get('token'),
      });
      showSnackbar({
        text: 'Password successfully changed',
        type: 'success',
      });
      navigate('/sign-in');
    } catch (e: unknown) {
      const validationErrors = getValidationIssueErrors(e);
      if (validationErrors.length > 0) {
        setSubmitError(validationErrors[0].message);
        return;
      }

      setSubmitError(getApiErrorMessage(e, 'Unable to reset password'));
    }
  }

  return (
    <Box sx={SignInSx}>
      <Paper
        sx={PaperSx}
        component={'form'}
        onSubmit={handleSubmit(handleLogin)}
      >
        <Typography sx={TitleSx}>Reset your password</Typography>
        <Typography sx={ExplanationTextSx}>
          Please create a new password for your account. To keep things secure,
          make sure it's at least 6 characters long.
        </Typography>

        <ControlledField
          name="newPassword"
          control={control}
          component={
            <TextField
              label="New password"
              placeholder="min. 6 characters"
              required
              autocompleteHtmlAttribute="new-password"
              fullWidth
              type={newPasswordIsVisible ? 'text' : 'password'}
              endAdornment={
                <Box
                  onClick={() => setNewPasswordIsVisible((visible) => !visible)}
                  sx={EyeContainerSx}
                >
                  {newPasswordIsVisible ? (
                    <EyeClosedIcon
                      data-testid={TestIds.ResetPassword.ClosedEyeIcon}
                    />
                  ) : (
                    <EyeIcon
                      data-testid={TestIds.ResetPassword.OpenedEyeIcon}
                    />
                  )}
                </Box>
              }
              data-testid={TestIds.ResetPassword.NewPassword}
            />
          }
        />

        <ControlledField
          name="confirmPassword"
          control={control}
          component={
            <TextField
              label="Confirm password"
              placeholder="Enter new password"
              fullWidth
              required
              autocompleteHtmlAttribute="new-password"
              sx={PasswordSx}
              type={confirmedPasswordIsVisible ? 'text' : 'password'}
              endAdornment={
                <Box
                  onClick={() =>
                    setConfirmedPasswordIsVisible((visible) => !visible)
                  }
                  sx={EyeContainerSx}
                >
                  {confirmedPasswordIsVisible ? (
                    <EyeClosedIcon
                      data-testid={TestIds.ResetPassword.ClosedEyeIcon}
                    />
                  ) : (
                    <EyeIcon
                      data-testid={TestIds.ResetPassword.OpenedEyeIcon}
                    />
                  )}
                </Box>
              }
              data-testid={TestIds.ResetPassword.ConfirmPassword}
            />
          }
        />
        <Button
          sx={SaveButtonSx}
          variant="contained"
          type="submit"
          data-testid={TestIds.ResetPassword.SaveNewPassword}
        >
          Save new password
        </Button>
        <Stack alignContent={'center'} alignItems={'center'}>
          <Link
            href="/sign-in"
            underline="none"
            sx={CancelSx}
            data-testid={TestIds.ResetPassword.Cancel}
          >
            Cancel
          </Link>
        </Stack>
        {submitError && (
          <Typography sx={ErrorMessageSx}>{submitError}</Typography>
        )}
      </Paper>
    </Box>
  );
}

export default ResetPassword;
